import * as Blockly from "blockly";
import AudioService from "./audio.service";
import { getSimulatorUrl } from "../common/utils";

let interpreter;
let simulatorFrame;

const workerUrl = new URL("./interpreter.service.js", import.meta.url);

const sendToSimulator = (payload) => {
    const simURL = new URL(getSimulatorUrl());
    if (simulatorFrame && simulatorFrame.contentWindow) {
        simulatorFrame.contentWindow.postMessage(payload, simURL.origin);
    }
};

const sendToInterpreter = (payload) => {
    if (interpreter) {
        interpreter.postMessage(payload);
    }
};

const startSimulation = async (simulator, program, onEvent) => {
    simulatorFrame = simulator.current;
    await startWebWorker();

    const payload = {
        type: "start",
    };
    sendToSimulator(payload);

    interpreter.postMessage({ program: program });
    interpreter.addEventListener("message", (event) => {
        handleWorkerMessage(event, onEvent);
    });
    interpreter.onerror = (error) => {
        killWebWorker();
        onEvent({ err: error.message });
    };
};

const stopSimulation = () => {
    AudioService.stopTone("B");
    killWebWorker();
    const payload = {
        type: "stop",
    };
    sendToSimulator(payload);
};

const resetSimulation = () => {
    killWebWorker();
    const payload = {
        type: "reset",
    };
    sendToSimulator(payload);
};

const startWebWorker = () => {
    return new Promise((resolve, reject) => {
        interpreter = new Worker(workerUrl);

        const messageHandler = (event) => {
            if (event.data === "ready") {
                interpreter.removeEventListener("message", messageHandler);
                resolve();
            }
        };
        interpreter.addEventListener("message", messageHandler);
    });
};

const killWebWorker = () => {
    if (interpreter) {
        try {
            interpreter.removeEventListener("message", handleWorkerMessage);
            interpreter.terminate();
        } catch (error) {
        } finally {
            interpreter = null;
        }
    }
};

const handleWorkerMessage = (event, onEvent) => {
    const { data } = event;

    // block highlighting
    if (data.type === "block") {
        if (data.state === "highlight") {
            window.highlightBlock(data.id);
        } else if (data.state === "unhighlight") {
            window.unHighlightBlock(data.id);
        }
    }
    if (data.type === "serialMessage") {
        onEvent({ serialMessage: data.value });
    }
    if (data.type == "sound") {
        const volume = 0.5;
        if (data.state == "hertz") {
            AudioService.startTone(data.buzzer, data.value, volume);
        } else if (data.state == "hertzTimeout") {
            AudioService.startToneForDuration(
                data.buzzer,
                data.value,
                data.timeout,
                volume
            );
        } else if (data.state == "hertzOff") {
            AudioService.muteTone(data.buzzer);
        }
    }
    if (data.type == "sensorData") {
        onEvent({ sensorData: data.values });
    }
    if (data.type === "simmessage") {
        const simData = data.detail;
        sendToSimulator(simData);
    }
    if (data.type === "stopProgram") {
        // stopProgram
        sendToSimulator({
            type: "stop",
        });
        killWebWorker();
        onEvent({ close: true });
    }
};

const checkAddons = (simulator, program, workspaceRef, isOpen) => {
    simulatorFrame = simulator.current;

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(program, "application/xml");
    const blocks = xmlDoc.getElementsByTagName("block");

    const addonBlockTypes = [
        "GAMEPAD_KNOB",
        "GAMEPAD_BUTTONS",
        "LCD",
        "FAN",
        "LED",
        "button_read",
        "light_sensor",
        "motion",
    ];

    const addonBlockTags = [
        "GAMEPAD",
        "GAMEPAD",
        "LCD",
        "FAN",
        "LED",
        "BUTTON",
        "LDR",
        "MOTION",
    ];

    const blockArray = Array.from(blocks).map((block) => ({
        type: block.getAttribute("type"),
        id: block.getAttribute("id"),
    }));

    const allAddonBlocks = blockArray.filter((block) =>
        addonBlockTypes.includes(block.type)
    );

    let addons = [];

    const updateSimulator = (addons) => {
        sendToSimulator({
            type: "updateAddons",
            addons: addons,
        });

        const disableBlocks = allAddonBlocks.filter(
            (block) =>
                !addons.includes(
                    addonBlockTags[addonBlockTypes.indexOf(block.type)]
                )
        );

        const enableBlocks = allAddonBlocks.filter((block) =>
            addons.includes(addonBlockTags[addonBlockTypes.indexOf(block.type)])
        );

        if (disableBlocks.length > 0 && isOpen) {
            if (workspaceRef.current) {
                Blockly.Events.disable();
                try {
                    disableBlocks.forEach((block) => {
                        const blockToDisable =
                            workspaceRef.current.getBlockById(block.id);
                        if (blockToDisable) {
                            blockToDisable.setEnabled(false);
                        }
                    });
                    enableBlocks.forEach((block) => {
                        const blockToDisable =
                            workspaceRef.current.getBlockById(block.id);
                        if (blockToDisable) {
                            blockToDisable.setEnabled(true);
                        }
                    });
                } finally {
                    Blockly.Events.enable();
                }
            }
        } else {
            Blockly.Events.disable();
            try {
                allAddonBlocks.forEach((block) => {
                    const blockToDisable = workspaceRef.current.getBlockById(
                        block.id
                    );
                    if (blockToDisable) {
                        blockToDisable.setEnabled(true);
                    }
                });
            } finally {
                Blockly.Events.enable();
            }
        }
    };

    const blockTypesSet = new Set(allAddonBlocks.map((block) => block.type));

    // Treat GAMEPAD_KNOB and GAMEPAD_BUTTONS as one kind of block
    const hasGamepad =
        blockTypesSet.has("GAMEPAD_KNOB") ||
        blockTypesSet.has("GAMEPAD_BUTTONS");
    const hasLCD = blockTypesSet.has("LCD");
    const hasFan = blockTypesSet.has("FAN");
    const hasLED = blockTypesSet.has("LED");
    const hasButton = blockTypesSet.has("button_read");
    const hasLightSensor = blockTypesSet.has("light_sensor");
    const hasMotion = blockTypesSet.has("motion");

    // Check combinations based on the logic
    if (hasLCD && hasGamepad) {
        addons = ["LCD", "GAMEPAD"];
    } else if (hasLCD) {
        addons = ["LCD"];
    } else if (hasGamepad) {
        addons = ["GAMEPAD"];
    } else if (hasFan) {
        addons = ["FAN"];
    }
    // Check for small component combos
    else if (hasMotion && hasButton) {
        addons = ["MOTION", "BUTTON"];
    } else if (hasMotion && hasLED) {
        addons = ["MOTION", "LED"];
    } else if (hasMotion && hasLightSensor) {
        addons = ["MOTION", "LDR"];
    } else if (hasButton && hasLightSensor) {
        addons = ["BUTTON", "LDR"];
    } else if (hasButton && hasLED) {
        addons = ["BUTTON", "LED"];
    } else if (hasLED && hasLightSensor) {
        addons = ["LED", "LDR"];
    }
    // Check for small component singles
    else if (hasMotion) {
        addons = ["MOTION"];
    } else if (hasLED) {
        addons = ["LED"];
    } else if (hasButton) {
        addons = ["BUTTON"];
    } else if (hasLightSensor) {
        addons = ["LDR"];
    } else {
        addons = [];
    }

    updateSimulator(addons);
    return addons;
};

const updateAddonValues = (payload) => {
    sendToInterpreter(payload);
    sendToSimulator({
        type: "addonValue",
        addon: payload.addonValue.addon,
        value: payload.addonValue.value,
        detail: payload.addonValue.detail || null,
    });
};

const SimulatorService = {
    updateAddonValues,
    sendToInterpreter,
    sendToSimulator,
    startSimulation,
    stopSimulation,
    resetSimulation,
    checkAddons,
};

export default SimulatorService;
